import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Company } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Plus, Search, Building2 } from 'lucide-react';
import { Input } from "../components/ui/input.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group.jsx";
import { Label } from "../components/ui/label.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";

export default function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      // Carrega o usuário atual para verificar permissões
      const userData = await User.me();
      setCurrentUser(userData);
      
      // Verifica se o usuário atual é interno (não cliente)
      if (userData.role !== 'client') {
        // Carrega todas as empresas
        const companiesData = await Company.list();
        setCompanies(companiesData);
      } else {
        // Redireciona para dashboard se for cliente
        navigate(createPageUrl('Dashboard'));
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
    setIsLoading(false);
  };

  // Filtra empresas com base no termo de pesquisa e filtro de status
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchTerm || 
      company.corporate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.trade_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.cnpj?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (!currentUser || currentUser.role === 'client') {
    return null; // Não exibe nada para clientes
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Empresas</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie as empresas clientes no sistema
          </p>
        </div>
        <Button
          onClick={() => navigate(createPageUrl('NewCompany'))}
          className="bg-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Filtros */}
        <Card className="md:col-span-1">
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar empresas..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="space-y-3">
                <Label>Status</Label>
                <RadioGroup
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="all-status" />
                    <Label htmlFor="all-status" className="cursor-pointer">
                      Todas
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="active" id="active-status" />
                    <Label htmlFor="active-status" className="cursor-pointer">
                      Ativas
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="inactive" id="inactive-status" />
                    <Label htmlFor="inactive-status" className="cursor-pointer">
                      Inativas
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de empresas */}
        <div className="md:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>
                {searchTerm || statusFilter !== 'all' 
                  ? `Resultados (${filteredCompanies.length})` 
                  : `Todas as empresas (${companies.length})`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array(5).fill(0).map((_, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-4 w-[150px]" />
                      </div>
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : filteredCompanies.length === 0 ? (
                <div className="text-center py-8">
                  <h3 className="text-lg font-medium text-gray-900">Nenhuma empresa encontrada</h3>
                  <p className="text-gray-500 mt-2">
                    Tente ajustar seus filtros ou criar uma nova empresa.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCompanies.map((company) => (
                    <div
                      key={company.id}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(createPageUrl(`ViewCompany?id=${company.id}`))}
                    >
                      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-gray-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {company.corporate_name}
                          </h3>
                          <Badge
                            variant={company.status === 'active' ? 'success' : 'secondary'}
                            className="ml-2"
                          >
                            {company.status === 'active' ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          {company.trade_name && (
                            <span className="truncate">{company.trade_name}</span>
                          )}
                          {company.cnpj && (
                            <span className="ml-2">{company.cnpj}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}